'use client';

import React from 'react';
import { Check, Loader2, SquarePen, XCircle } from 'lucide-react';
import { Button } from '@/core/presentation/components/ui/Button';
import { Input } from '@/core/presentation/components/ui/Input';
import { OtpInput } from '@/core/presentation/components/ui/OtpInput';
import { SocialButton } from '@/core/presentation/components/ui/SocialButton';
import {
  LoginFormOtpFeedback,
  LoginFormSocialProvider,
} from '@/features/authentication/presentation/composables/enums/login-form.enums';
import {
  LOGIN_FORM_CHANGE_NUMBER,
  LOGIN_FORM_CODE_SENT_PREFIX,
  LOGIN_FORM_OR_CONTINUE,
  LOGIN_FORM_OTP_LABEL,
  LOGIN_FORM_OTP_STATUS_INVALID,
  LOGIN_FORM_OTP_STATUS_VERIFIED,
  LOGIN_FORM_OTP_STATUS_VERIFYING,
  LOGIN_FORM_PHONE_LABEL,
  LOGIN_FORM_PHONE_PLACEHOLDER,
  LOGIN_FORM_RESEND,
  LOGIN_FORM_RESEND_IN,
  LOGIN_FORM_SEND_CODE,
  LOGIN_FORM_SEND_LOADING,
  LOGIN_BRIDGE,
} from '@/features/authentication/presentation/composables/constants/login.constants';
import { useLoginForm } from '@/features/authentication/presentation/composables/hooks/loginFormHook';

export interface LoginFormProps {
  authLoading: boolean;
  onRequestSendCode: (whatsappNationalDigits: string) => Promise<boolean>;
  onResendCode: (whatsappNationalDigits: string) => Promise<boolean>;
  onVerifyCode: (code: string) => Promise<boolean>;
  onPhoneInputChange: () => void;
  onChangeNumberStep: () => void;
  onGoogleClick: () => void | Promise<void>;
  onFacebookClick: () => void | Promise<void>;
  /** Chamado após verificação do código com sucesso; recebe dígitos nacionais do WhatsApp. */
  onVerified?: (whatsappNationalDigits: string) => void;
}

export const LoginForm: React.FC<LoginFormProps> = (props) => {
  const {
    authLoading,
    isCodeStep,
    code,
    setCode,
    otpBusy,
    otpFeedback,
    resendCooldownSeconds,
    phoneDisplay,
    phoneFormattedValue,
    sendDisabled,
    handlePhoneChange,
    handleSendCode,
    handleResendCode,
    handleChangeNumber,
    onGoogleClick,
    onFacebookClick,
  } = useLoginForm(props);

  const recaptchaAnchor = (
    <div key="recaptcha-anchor" id={LOGIN_BRIDGE.RECAPTCHA_CONTAINER_ID} className="sr-only" aria-hidden />
  );

  return (
    <div>
      {isCodeStep ?
        <div className="space-y-2">
          <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-white/80">
            <span className="shrink-0">{LOGIN_FORM_CODE_SENT_PREFIX}</span>
            <span className="inline-flex min-w-0 max-w-full flex-nowrap items-center gap-2">
              <strong className="whitespace-nowrap text-white">{phoneDisplay}</strong>
              <button
                type="button"
                onClick={handleChangeNumber}
                disabled={otpBusy}
                aria-label={LOGIN_FORM_CHANGE_NUMBER}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-cyan transition-colors hover:bg-white/10 hover:text-cyan-light focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-navy-dark disabled:pointer-events-none disabled:opacity-50"
              >
                <SquarePen className="h-4 w-4" aria-hidden />
              </button>
            </span>
          </p>
          <OtpInput value={code} onChange={setCode} label={LOGIN_FORM_OTP_LABEL} disabled={otpBusy} />
          <div className="min-h-[1.5rem]" role="status" aria-live="polite">
            {otpFeedback === LoginFormOtpFeedback.Verifying ?
              <p className="flex items-center gap-2 text-sm text-white/85">
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-cyan" aria-hidden />
                {LOGIN_FORM_OTP_STATUS_VERIFYING}
              </p>
            : otpFeedback === LoginFormOtpFeedback.Error ?
              <p className="flex items-center gap-2 text-sm text-red-400">
                <XCircle className="h-4 w-4 shrink-0" aria-hidden />
                {LOGIN_FORM_OTP_STATUS_INVALID}
              </p>
            : otpFeedback === LoginFormOtpFeedback.Success ?
              <p className="flex items-center gap-2 text-sm font-medium text-emerald-400">
                <Check className="h-4 w-4 shrink-0" aria-hidden />
                {LOGIN_FORM_OTP_STATUS_VERIFIED}
              </p>
            : null}
          </div>
          <p className="text-white/60 text-sm text-right">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendCooldownSeconds > 0 || otpBusy}
              className="text-cyan font-medium hover:text-cyan-light focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan rounded disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-cyan"
            >
              {resendCooldownSeconds > 0
                ? LOGIN_FORM_RESEND_IN(resendCooldownSeconds)
                : LOGIN_FORM_RESEND}
            </button>
          </p>
        </div>
      : <form onSubmit={handleSendCode} className="space-y-4">
          <Input
            type="tel"
            name="phone"
            label={LOGIN_FORM_PHONE_LABEL}
            placeholder={LOGIN_FORM_PHONE_PLACEHOLDER}
            autoComplete="tel"
            inputMode="tel"
            value={phoneFormattedValue}
            onChange={handlePhoneChange}
            fullWidth
            required
          />
          <Button
            type="submit"
            fullWidth
            size="lg"
            disabled={sendDisabled}
            loading={authLoading}
            loadingLabel={LOGIN_FORM_SEND_LOADING}
          >
            {LOGIN_FORM_SEND_CODE}
          </Button>

          <div className="flex items-center gap-3 my-6">
            <span className="flex-1 border-t border-white/20" />
            <span className="text-sm text-white/60 shrink-0">{LOGIN_FORM_OR_CONTINUE}</span>
            <span className="flex-1 border-t border-white/20" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <SocialButton
              provider={LoginFormSocialProvider.Google}
              onClick={onGoogleClick}
              fullWidth
              disabled={authLoading}
            />
            <SocialButton
              provider={LoginFormSocialProvider.Facebook}
              onClick={onFacebookClick}
              fullWidth
              disabled={authLoading}
            />
          </div>
        </form>
      }
      {recaptchaAnchor}
    </div>
  );
};
